import type { ShipTime } from '@felix/domain';
import { UnstyledButton } from '@mantine/core';
import { IconAlertTriangle, IconArrowBackUp, IconChecklist } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import { followLink, useShell } from '../../shell-context.js';
import work from '../pms/PmsWork.module.css';
import classes from './Forms.module.css';
import { EMPTY_HISTORY_FILTER, historySearch } from './history.js';
import { FORM_HISTORY_PATH, recordPath } from './record-model.js';
import { recordTime } from './status.js';
import type {
  ApprovalAlert,
  ApprovalCard,
  FormRecordBellNotice,
  FormReturnedNotice,
} from './types.js';

/** Kartta gösterilen en çok satır; tamamı Form History'de "Awaiting my approval". */
const LIMIT = 5;

/**
 * Panoda "Pending approvals" kartı (mimari belge §7.3; ADR 0042 §16, Ek U29): kişinin karar
 * verebileceği ISM form adımları; süresi geçenler önce ve kırmızı [MEPC.312 4.3.10]. Altında gri
 * "Upcoming" satırları: zincirde kişiyi bekleyen, sırası gelmemiş kayıtlar (ADR 0069). Gemide gemi
 * adımları (gemi saati), ofiste ofis adımları (UTC). Yetkisi olmayan kişide kart çizilmez.
 */
export function FormApprovalsCard({
  card,
  timeZone,
  vesselName,
}: {
  card: ApprovalCard | undefined;
  /** Gemide geminin saat dilimi; ofiste `null` (UTC). */
  timeZone: ShipTime | null;
  /** Ofiste gemi adı (kimlikten). */
  vesselName?: (vesselId: string) => string | undefined;
}) {
  const { t } = useTranslation();
  const shell = useShell();
  if (!card) return null;
  const upcoming = card.upcoming ?? [];
  const all = `${FORM_HISTORY_PATH}${historySearch({ ...EMPTY_HISTORY_FILTER, awaiting: true })}`;
  return (
    <Panel
      title={t('forms.approvals.title')}
      icon={<IconChecklist size={17} stroke={1.8} />}
      extra={
        <a
          href={all}
          className={classes.allRecords}
          onClick={(event) => shell && followLink(event, all, shell.navigate)}
        >
          {t('forms.approvals.viewAll')}
        </a>
      }
    >
      <p className={classes.approvalCounts}>
        <StatusBadge tone={card.pending > 0 ? 'warning' : 'success'}>
          {t('forms.approvals.pending', { count: card.pending })}
        </StatusBadge>
        {card.overdue > 0 ? (
          <StatusBadge tone="danger">
            {t('forms.approvals.overdue', { count: card.overdue })}
          </StatusBadge>
        ) : null}
      </p>
      {card.items.length === 0 && upcoming.length === 0 ? (
        <EmptyState title={t('forms.approvals.none')} />
      ) : card.items.length === 0 ? null : (
        <ul className={classes.approvalCardList}>
          {card.items.slice(0, LIMIT).map((item) => {
            const path = recordPath(item.recordId);
            const vessel = vesselName?.(item.vesselId);
            return (
              <li
                key={`${item.recordId}-${item.step}`}
                className={classes.approvalCardItem}
                data-overdue={item.kind === 'approval_overdue' || undefined}
              >
                <a
                  href={path}
                  className={classes.recordLink}
                  onClick={(event) => shell && followLink(event, path, shell.navigate)}
                >
                  {item.recordNo}
                </a>
                <span className={classes.approvalCardText} title={item.documentTitle}>
                  {item.docNo} - {item.documentTitle}
                  {vessel ? ` · ${vessel}` : ''}
                </span>
                <span className={classes.muted}>
                  {t('forms.approvals.step', { step: item.step, steps: item.steps })} ·{' '}
                  {recordTime(item.since, timeZone)}
                </span>
                {item.kind === 'approval_overdue' ? (
                  <StatusBadge tone="danger">{t('forms.revisions.overdue')}</StatusBadge>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {upcoming.length > 0 ? (
        // Sırası gelmemiş kayıtlar gri (ADR 0069): zincirde kişiyi bekler, önce başka rol karar verir
        <ul className={classes.approvalCardList} aria-label={t('forms.approvals.upcoming')}>
          {upcoming.slice(0, LIMIT).map((item) => {
            const path = recordPath(item.recordId);
            const vessel = vesselName?.(item.vesselId);
            return (
              <li
                key={`upcoming-${item.recordId}-${item.step}`}
                className={classes.approvalCardItem}
                data-upcoming="true"
              >
                <a
                  href={path}
                  className={classes.recordLink}
                  onClick={(event) => shell && followLink(event, path, shell.navigate)}
                >
                  {item.recordNo}
                </a>
                <span className={classes.approvalCardText} title={item.documentTitle}>
                  {item.docNo} - {item.documentTitle}
                  {vessel ? ` · ${vessel}` : ''}
                </span>
                <span className={classes.muted}>
                  {t('forms.approvals.step', { step: item.step, steps: item.steps })} ·{' '}
                  {t('forms.steps.waitingFor', { role: item.waitingRole ?? '—' })}
                </span>
                <StatusBadge tone="neutral">{t('forms.approvals.upcoming')}</StatusBadge>
              </li>
            );
          })}
        </ul>
      ) : null}
    </Panel>
  );
}

/**
 * Zilin ISM form maddeleri (ADR 0042 §3, §16; Ek U33): karar verilecek adımlar (süresi geçenler
 * kırmızı) ve kendi taslağının revizyonu yürürlükten kalktı bildirimi. Madde kayıt sayfasını açar.
 */
export function FormBellList({
  items,
  notices = [],
  onOpen,
}: {
  items: readonly Pick<
    ApprovalAlert,
    'kind' | 'recordId' | 'recordNo' | 'docNo' | 'documentTitle' | 'step'
  >[];
  notices?: readonly FormRecordBellNotice[];
  onOpen: (recordId: string) => void;
}) {
  const { t } = useTranslation();
  if (items.length === 0 && notices.length === 0) return null;
  return (
    <ul className={work.bellList} aria-label={t('forms.approvals.title')}>
      {items.map((item) => (
        <li key={`${item.recordId}-${item.step}`}>
          <UnstyledButton className={work.bellItem} onClick={() => onOpen(item.recordId)}>
            <span className={work.bellIcon} aria-hidden>
              <IconChecklist size={16} />
            </span>
            <span>
              <strong>
                {t(
                  item.kind === 'approval_overdue'
                    ? 'forms.approvals.bellOverdue'
                    : 'forms.approvals.bellPending',
                  { record: item.recordNo },
                )}
              </strong>
              <span className={work.note}>
                {item.docNo} - {item.documentTitle}
              </span>
            </span>
          </UnstyledButton>
        </li>
      ))}
      {notices.map((notice) =>
        notice.kind === 'record_returned' ? (
          // İade gönderene (ortak 2026-10-22; ADR 0116 §1): gerekçe ve iade eden
          <li key={`returned-${notice.recordId}`}>
            <UnstyledButton className={work.bellItem} onClick={() => onOpen(notice.recordId)}>
              <span className={work.bellIcon} aria-hidden>
                <IconArrowBackUp size={16} />
              </span>
              <span>
                <strong>{t('forms.approvals.bellReturned', { record: notice.recordNo })}</strong>
                <span className={work.note}>
                  {notice.docNo} - {notice.documentTitle}
                </span>
                <span className={work.note}>{returnedLine(t as Translate, notice)}</span>
              </span>
            </UnstyledButton>
          </li>
        ) : (
          <li key={`notice-${notice.recordId}`}>
            <UnstyledButton className={work.bellItem} onClick={() => onOpen(notice.recordId)}>
              <span className={work.bellIcon} aria-hidden>
                <IconAlertTriangle size={16} />
              </span>
              <span>
                <strong>{t('forms.approvals.bellNotice', { record: notice.recordNo })}</strong>
                <span className={work.note}>
                  {notice.docNo} - {notice.documentTitle}
                </span>
              </span>
            </UnstyledButton>
          </li>
        ),
      )}
    </ul>
  );
}

type Translate = (key: string, values?: Record<string, unknown>) => string;

/** "Master Ahmet Kaya: Wrong area" (iade eden ve gerekçe). */
export function returnedLine(t: Translate, notice: FormReturnedNotice): string {
  const by = [notice.returnedRole, notice.returnedBy].filter(Boolean).join(' ') || '—';
  return t('forms.returned.by', {
    by,
    reason: notice.reason?.trim() ? notice.reason : t('forms.returned.noReason'),
  });
}
